/**
 *
 * FundSuggestor
 *
 */

// @flow

import { Cell, Grid, GridContainer } from '@fil-global/eiswurfel';
import _first from 'lodash/first';
import _noop from 'lodash/noop';
import _toLower from 'lodash/toLower';
import _trim from 'lodash/trim';
import type { Node } from 'react';
import React, { useEffect } from 'react';
import { Highlighter, Typeahead } from 'react-bootstrap-typeahead';

import { useStaticLabelsContext } from '../../global/context';
import { jsonpClient } from '../../utils';
import {
  FUNDS_FETCH_FAILURE,
  FUNDS_FETCH_REQUEST,
  FUNDS_FETCH_SUCCESS,
} from './FundSuggestor.constants';
import { useFundDispatchContext, useFundStateContext } from './FundSuggestor.context';
import type { Props } from './types';

const filterByCallback = (option: Object, props: Object): boolean => {
  const text = _toLower(_trim(props.text));

  return (
    _toLower(option.FN).includes(text) ||
    _toLower(option.ISIN).includes(text) ||
    _toLower(option.WKN).includes(text)
  );
};

const renderMenuItemChildren = (option: Object, props: Object): Array<Node> => {
  const { staticLabels } = props;
  const text = _trim(props.text);

  return [
    <Highlighter key="fund-name" search={text}>
      {option.FN}
    </Highlighter>,
    <div key="isin-and-wkn">
      <span>{staticLabels.fundSuggestor.isin}:</span>{' '}
      <Highlighter key="ISIN" search={text}>
        {option.ISIN}
      </Highlighter>
      <span> / </span>
      <span>{staticLabels.fundSuggestor.wkn}:</span>{' '}
      <Highlighter key="WKN" search={text}>
        {option.WKN}
      </Highlighter>
    </div>,
  ];
};

const FundSuggestor = ({
  fieldName,
  fieldValue,
  goToNextQuestion,
  isEditMode,
  onValueChange,
}: Props): Node => {
  const state = useFundStateContext();
  const dispatch = useFundDispatchContext();
  const staticLabels = useStaticLabelsContext();

  const { funds, isFetched } = state;

  // Fetching the funds from FWW if funds are not available.
  useEffect(() => {
    const fetchFunds = async () => {
      dispatch({ type: FUNDS_FETCH_REQUEST });

      try {
        const response = await jsonpClient(
          'https://cf.fww.de/fidelity/json-feeds/fondsfinder-mini-with-etfs.json',
          '_fondsSearchMinData'
        );

        dispatch({ type: FUNDS_FETCH_SUCCESS, data: response });
      } catch (error) {
        dispatch({ type: FUNDS_FETCH_FAILURE, error });
      }
    };

    if (!isFetched) {
      fetchFunds();
    }
  }, [dispatch, isFetched]);

  const onFundSelect = value => {
    const fund = _first(value);

    onValueChange(fieldName, fund);
    goToNextQuestion();
  };

  return (
    <GridContainer variation="full" extraClasses="fund-suggestor">
      {isEditMode && (
        <Grid>
          <Cell extraClasses="fund-search">
            <Typeahead
              a11yNumSelected={_noop}
              emptyLabel={staticLabels.fundSuggestor.emptyLabel}
              filterBy={filterByCallback}
              id="fundSearch"
              labelKey="FN"
              minLength={3}
              onChange={onFundSelect}
              options={funds}
              placeholder={staticLabels.fundSuggestor.placeholderText}
              renderMenuItemChildren={(option, props) =>
                renderMenuItemChildren(option, { ...props, staticLabels })
              }
            />
          </Cell>
        </Grid>
      )}

      {!isEditMode && (
        <Grid extraClasses="selected-view">
          <Cell>
            <strong>{fieldValue.FN}</strong>
          </Cell>
          <Cell>
            <span>{staticLabels.fundSuggestor.isin}:</span> {fieldValue.ISIN}
            <span> / </span>
            <span>{staticLabels.fundSuggestor.wkn}:</span> {fieldValue.WKN}
          </Cell>
        </Grid>
      )}
    </GridContainer>
  );
};

FundSuggestor.defaultProps = {
  goToNextQuestion: _noop,
  onValueChange: _noop,
};

export default FundSuggestor;
